/** Beautiful dashboard — replaces the bare session-list placeholder.
 *
 * One round-trip to `/v1/dashboard/stats` returns:
 *   - aggregate counts (sessions / evidence / recordings / campaigns)
 *   - per-status breakdown
 *   - five most-recent sessions (already fully serialised)
 *
 * So the page renders without a follow-up `/v1/sessions/` fetch.
 *
 * Layout:
 *   greeting strip → quick actions
 *   stat cards (one row, one column per card)
 *   status breakdown bar
 *   recent sessions table
 *   billing banner (if past-due, etc.)
 */
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { AppLayout } from "@/components/AppLayout";
import { PermissionGate } from "@/components/PermissionGate";
import { SessionsLineChart } from "@/components/SessionsLineChart";
import { StatCard } from "@/components/StatCard";
import { OperatorAvailabilityBoard } from "@/modules/admin";
import { LiveInspectionsBoard } from "@/modules/sessions/LiveInspectionsBoard";
import { StatusDonut } from "@/components/StatusDonut";
import { StatusPill } from "@/components/StatusPill";
import { ApiPaths, api } from "@/lib/api";
import { useMe } from "@/lib/auth";
import { formatDuration, formatRelative } from "@/lib/format";
import { useHasPermission } from "@/lib/permissions";
import type { DashboardStats } from "@/lib/types";
import { BillingBanner } from "@/modules/billing";
import { OperatorDashboard, TrySampleButton } from "@/modules/dashboard";


export default function DashboardPage() {
  const { t } = useTranslation();
  const me = useMe();
  const userEmail = me.data?.user.email;

  // An operator (can run sessions but not administer the org) gets their
  // OWN stats, not the org-wide dashboard (OPERATOR_OPS #6).
  const canReadSessions = useHasPermission("session.read");
  const isOrgAdmin = useHasPermission("org.update");
  const isOperatorView = canReadSessions && !isOrgAdmin;

  const q = useQuery({
    queryKey: ["dashboard-stats"],
    queryFn: async () => {
      const res = await api.get<DashboardStats>(ApiPaths.dashboardStats);
      return res.data;
    },
    refetchInterval: 60_000,
    placeholderData: (prev) => prev,
    enabled: !isOperatorView,
  });

  return (
    <AppLayout
      title={t("dashboard.title")}
      description={t("dashboard.subhead")}
      headerAction={
        <PermissionGate perm="session.create">
          <div className="flex items-center gap-2">
            <TrySampleButton />
          </div>
        </PermissionGate>
      }
    >
      <PermissionGate perm="billing.read">
        <BillingBanner />
      </PermissionGate>

      {/* The stat cards below degrade to "—" placeholders while q.data is
          undefined, which reads as "loading" (fine) but is indistinguishable
          from a genuine fetch failure (not fine — the operator has no way to
          tell "no sessions yet" from "the dashboard is broken"). */}
      {!isOperatorView && q.isError && (
        <p className="card text-sm text-cvp-danger">
          {t("dashboard.loadError", "Could not load dashboard stats.")}
        </p>
      )}

      {/* Greeting card — small, but warmer than dropping straight into stats. */}
      {userEmail && (
        <p className="text-sm text-nb-text-2">
          <span className="font-medium text-nb-text">
            {t("dashboard.greeting")}, {userEmail}.
          </span>
        </p>
      )}

      {/* Operator view: their own stats, not the org-wide aggregates. */}
      {isOperatorView && <OperatorDashboard />}

      {/* Stat cards. 1-column on mobile, 2-up on tablet, 4-up on desktop. */}
      {!isOperatorView && (
      <>
      {/* Two cards since Evidence + Recordings came out (#247). This
          was still `lg:grid-cols-4`, so the pair sat in the left half
          of the row with the right half blank — it read as broken
          layout rather than as a deliberate removal. Two columns also
          lines the row up with the two-column sections below it. */}
      <section className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <StatCard
          label={t("dashboard.statSessions")}
          value={q.data?.sessions.total ?? "—"}
          delta={
            q.data
              ? t("dashboard.statSessionsHint", {
                  count: q.data.sessions.this_month,
                })
              : undefined
          }
          tone="info"
          icon={
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <rect x="3" y="6" width="13" height="12" rx="2" stroke="currentColor" strokeWidth="1.8"/>
              <path d="M16 10 l5-3 v10 l-5-3" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/>
            </svg>
          }
        />
        {/* Evidence + Recordings removed 2026-09-23 on request. They
            counted artefacts rather than work done, so they crowded the
            row without answering anything an operator opens the
            dashboard to ask. The data is unchanged and still reachable
            per session; only these two headline cards are gone. */}
        <StatCard
          label={t("dashboard.statCampaigns")}
          value={q.data?.campaigns.total ?? "—"}
          delta={
            q.data
              ? t("dashboard.statCampaignsHint", {
                  count: q.data.campaigns.active,
                })
              : undefined
          }
          tone="warn"
          icon={
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M4 5h12l4 7-4 7H4z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/>
              <path d="M4 5v14" stroke="currentColor" strokeWidth="1.8"/>
            </svg>
          }
        />
      </section>

      {/* Live inspections (beneficiary in a call) + operator availability. */}
      <section className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <LiveInspectionsBoard />
        <OperatorAvailabilityBoard />
      </section>

      {/* Charts row — line chart (sessions over time) + status donut. */}
      {q.data && (
        <section className="grid grid-cols-1 gap-4 lg:grid-cols-[2fr_1fr]">
          <div className="card">
            <header className="mb-3 flex items-baseline justify-between">
              <h2 className="text-sm font-semibold text-nb-text">
                {t("dashboard.chartSessions30d")}
              </h2>
              <span className="text-xs text-nb-text-3">
                {t("dashboard.chartSessions30dHint")}
              </span>
            </header>
            <SessionsLineChart
              data={q.data.sessions_timeseries ?? []}
              label={t("dashboard.chartSessions30d")}
            />
          </div>
          <div className="card">
            <h2 className="mb-3 text-sm font-semibold text-nb-text">
              {t("dashboard.statusBreakdown")}
            </h2>
            <StatusDonut
              ariaLabel={t("dashboard.statusBreakdownAria")}
              closed={q.data.sessions.by_status.closed}
              live={q.data.sessions.by_status.live}
              created={q.data.sessions.by_status.created}
              expired={q.data.sessions.by_status.expired}
              labels={{
                closed: t("dashboard.byStatusClosed"),
                live: t("dashboard.byStatusLive"),
                created: t("dashboard.byStatusCreated"),
                expired: t("dashboard.byStatusExpired"),
              }}
            />
          </div>
        </section>
      )}

      {/* Recent sessions table. */}
      <section className="card">
        <header className="mb-4 flex items-center justify-between gap-3">
          <h2 className="text-base font-semibold text-nb-text">
            {t("dashboard.recentSessions")}
          </h2>
          <Link
            to="/sessions"
            className="text-sm text-nb-mint hover:underline"
          >
            {t("dashboard.viewAllSessions")} →
          </Link>
        </header>
        {q.isLoading && (
          <p className="text-sm text-nb-text-2">{t("dashboard.loading")}</p>
        )}
        {q.data && q.data.recent_sessions.length === 0 && (
          <p className="text-sm text-nb-text-2">{t("dashboard.noSessions")}</p>
        )}
        {q.data && q.data.recent_sessions.length > 0 && (
          <ul className="divide-y divide-nb-border">
            {q.data.recent_sessions.map((s) => (
              <li key={s.id} className="-mx-2 rounded hover:bg-nb-surface/40">
                {/* Whole row clickable; finished sessions land straight on
                    the media detail page, live/pending on the session page. */}
                <Link
                  to={
                    s.status === "closed"
                      ? `/sessions/${s.id}/media`
                      : `/sessions/${s.id}`
                  }
                  className="flex items-center justify-between gap-3 px-2 py-3"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline gap-2">
                      <StatusPill status={s.status} />
                      <span className="truncate text-sm font-medium text-nb-text">
                        {s.title || s.notes || s.campaign_name || s.id.slice(0, 8)}
                      </span>
                    </div>
                    <div className="mt-1 text-xs text-nb-text-3">
                      {formatRelative(s.started_at || s.created_at)}
                      {s.duration_seconds != null && (
                        <> · {formatDuration(s.duration_seconds)}</>
                      )}
                      {s.operator_email && <> · {s.operator_email}</>}
                    </div>
                  </div>
                  <div className="hidden sm:flex items-center gap-3 text-xs text-nb-text-2">
                    <span>{s.participant_count}p</span>
                    <span>{s.evidence_count}e</span>
                    {s.has_recording && (
                      <span className="rounded bg-rose-500/15 px-1.5 py-0.5 font-mono text-rose-300">
                        {t("sessionsList.recordingBadge")}
                      </span>
                    )}
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
      </>
      )}
    </AppLayout>
  );
}


